/**
 * User Media model interfaces matching the User Media API specification
 * 
 * @remarks
 * These models correspond to the User Media API endpoints at `/api/user-medias`
 * Supports multi-size image URLs, soft delete, batch operations, and organization scoping.
 * 
 * @see USER_MEDIA_API_REFERENCE.md for complete API documentation
 */

import { BaseEntity, UserRef, OrganizationRef } from './base';

/**
 * Media URLs for different device sizes
 */
export interface MediaUrls {
  /**
   * Large size URL (required, must be valid URL)
   */
  large: string;

  /**
   * Medium size URL (required, must be valid URL)
   */
  medium: string;

  /**
   * Small size URL (required, must be valid URL)
   */
  small: string;

  /**
   * Mobile size URL (required, must be valid URL)
   */
  mobile: string;
}

/**
 * Core UserMedia model matching API schema
 * 
 * @remarks
 * Represents media with unpopulated references.
 * For populated version, use UserMediaPopulated.
 */
export interface UserMedia extends BaseEntity {
  /**
   * File name (1-255 characters)
   */
  fileName: string;

  /**
   * Organization ID this media belongs to
   */
  orgId: string;

  /**
   * Multi-size URLs for the media
   */
  urls: MediaUrls;

  /**
   * User ID who uploaded the media
   */
  userId: string;

  /**
   * Description of the media (optional, max 1000 characters)
   */
  description?: string;

  /**
   * Caption for the media (optional, max 500 characters)
   */
  caption?: string;

  /**
   * File size in bytes. null for images uploaded before this release.
   */
  fileSize?: number | null;

  /**
   * Soft delete flag
   * @default false
   */
  isDeleted: boolean;

  /**
   * Timestamp when media was soft deleted
   * Only present when isDeleted is true
   */
  deletedAt?: Date;
}

/**
 * UserMedia with populated references
 * 
 * @remarks
 * Returned by most User Media API endpoints.
 * Includes populated organization and user details.
 */
export interface UserMediaPopulated extends Omit<UserMedia, 'orgId' | 'userId'> {
  /**
   * Populated organization reference
   */
  orgId: OrganizationRef;

  /**
   * Populated user reference
   */
  userId: UserRef;
}

/**
 * Data Transfer Object for creating a single media
 * 
 * @remarks
 * Used in POST /api/user-medias
 */
export interface CreateUserMediaDto {
  /**
   * File name (required, 1-255 characters)
   */
  fileName: string;

  /**
   * Organization ID (required, MongoDB ObjectId)
   */
  orgId: string;

  /**
   * Multi-size URLs (required)
   */
  urls: MediaUrls;

  /**
   * Description of the media (optional, max 1000 characters)
   */
  description?: string;

  /**
   * Caption for the media (optional, max 500 characters)
   */
  caption?: string;

  /**
   * File size in bytes for storage usage tracking
   */
  fileSize?: number;
}

/**
 * Data Transfer Object for batch creating medias
 * 
 * @remarks
 * Used in POST /api/user-medias/batch
 */
export interface BatchCreateUserMediaDto {
  /**
   * Array of media items to create
   * Minimum 1 item required
   */
  medias: CreateUserMediaDto[];
}

/**
 * Data Transfer Object for updating a media
 * 
 * @remarks
 * Used in PUT /api/user-medias/:id
 * All fields are optional
 */
export interface UpdateUserMediaDto {
  /**
   * Updated file name (optional, 1-255 characters)
   */
  fileName?: string;

  /**
   * Updated multi-size URLs (optional)
   */
  urls?: MediaUrls;

  /**
   * Updated description (optional, max 1000 characters)
   */
  description?: string;

  /**
   * Updated caption (optional, max 500 characters)
   */
  caption?: string;
}

/**
 * Query parameters for getting all medias
 * 
 * @remarks
 * Used in GET /api/user-medias
 */
export interface GetAllMediasQueryParams {
  /**
   * Page number for pagination
   * @default 1
   * @min 1
   */
  page?: number;

  /**
   * Items per page
   * @default 10
   * @min 1
   */
  limit?: number;

  /**
   * Filter by file name (1-255 characters)
   */
  fileName?: string;

  /**
   * Filter by organization ID
   */
  orgId?: string;

  /**
   * Filter by start date (ISO string)
   */
  startDate?: string;

  /**
   * Filter by end date (ISO string)
   */
  endDate?: string;
}

/**
 * Query parameters for getting medias by organization
 * 
 * @remarks
 * Used in GET /api/user-medias/organization/:orgId
 */
export interface GetMediasByOrganizationQueryParams {
  /**
   * Page number for pagination
   * @default 1
   */
  page?: number;

  /**
   * Items per page
   * @default 10
   */
  limit?: number;
}

/**
 * Query parameters for searching medias
 * 
 * @remarks
 * Used in GET /api/user-medias/search
 */
export interface SearchMediasQueryParams {
  /**
   * Search query (required, 1-100 characters)
   */
  q: string;

  /**
   * Organization ID to search within (required)
   */
  orgId: string;

  /**
   * Page number for pagination
   * @default 1
   */
  page?: number;

  /**
   * Items per page
   * @default 10
   */
  limit?: number;
}

/**
 * Pagination metadata for media lists
 */
export interface MediaPagination {
  /**
   * Total number of items
   */
  total: number;

  /**
   * Current page number
   */
  page: number;

  /**
   * Items per page
   */
  limit: number;

  /**
   * Total number of pages
   */
  pages: number;
}

/**
 * Media statistics for an organization
 */
export interface MediaStats {
  /**
   * Total number of medias (active + deleted)
   */
  totalMedias: number;

  /**
   * Number of active medias
   */
  activeMedias: number;

  /**
   * Number of deleted medias
   */
  deletedMedias: number;

  /**
   * Total number of users who uploaded medias
   */
  totalUsers: number;

  /**
   * Media count grouped by user
   */
  mediasByUser: Array<{
    _id: string;
    count: number;
  }>;

  /**
   * Number of uploads in last 30 days
   */
  recentUploads: number;

  /**
   * Date of most recent upload
   */
  lastUploadDate: Date;
}

/**
 * API response wrapper for single media creation
 */
export interface CreateMediaResponse {
  status: 'success';
  message: 'Media created successfully';
  data: {
    media: UserMediaPopulated;
  };
}

/**
 * API response wrapper for batch media creation
 */
export interface BatchCreateMediaResponse {
  status: 'success';
  message: string; // e.g., "5 medias created successfully"
  data: {
    medias: UserMediaPopulated[];
    count: number;
  };
}

/**
 * API response wrapper for media list
 */
export interface MediaListResponse {
  status: 'success';
  data: {
    medias: UserMediaPopulated[];
    pagination: MediaPagination;
  };
}

/**
 * API response wrapper for single media retrieval
 */
export interface GetMediaResponse {
  status: 'success';
  data: {
    media: UserMediaPopulated;
  };
}

/**
 * API response wrapper for media update
 */
export interface UpdateMediaResponse {
  status: 'success';
  message: 'Media updated successfully';
  data: {
    media: UserMediaPopulated;
  };
}

/**
 * API response wrapper for media deletion
 */
export interface DeleteMediaResponse {
  status: 'success';
  message: 'Media deleted successfully';
}

/**
 * API response wrapper for media restoration (admin only)
 */
export interface RestoreMediaResponse {
  status: 'success';
  message: 'Media restored successfully';
  data: {
    media: UserMediaPopulated;
  };
}

/**
 * API response wrapper for media statistics
 */
export interface MediaStatsResponse {
  status: 'success';
  data: {
    stats: MediaStats;
  };
}
